import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError, timeout, retry } from 'rxjs';
import { buildUrl, API_TIMEOUT_MS, API_RETRY_COUNT } from './api.config';

export type QueryParams = Record<string, string | number | boolean | null | undefined>;

export interface ApiError {
  status: number;
  message: string;
  path?: string;
  timestamp?: string;
  details?: any;
}

@Injectable({ providedIn: 'root' })
export class ApiService {
  constructor(private http: HttpClient) {}

  get<T>(path: string, params?: QueryParams): Observable<T> {
    return this.http
      .get<T>(buildUrl(path), { params: this.makeParams(params), withCredentials: true })
      .pipe(timeout(API_TIMEOUT_MS), retry(API_RETRY_COUNT), catchError(this.handleError));
  }

  post<T>(path: string, body: unknown, params?: QueryParams): Observable<T> {
    return this.http
      .post<T>(buildUrl(path), body, { params: this.makeParams(params), withCredentials: true })
      .pipe(timeout(API_TIMEOUT_MS), retry(API_RETRY_COUNT), catchError(this.handleError));
  }

  put<T>(path: string, body: unknown, params?: QueryParams): Observable<T> {
    return this.http
      .put<T>(buildUrl(path), body, { params: this.makeParams(params), withCredentials: true })
      .pipe(timeout(API_TIMEOUT_MS), retry(API_RETRY_COUNT), catchError(this.handleError));
  }

  delete<T>(path: string, params?: QueryParams): Observable<T> {
    return this.http
      .delete<T>(buildUrl(path), { params: this.makeParams(params), withCredentials: true })
      .pipe(timeout(API_TIMEOUT_MS), retry(API_RETRY_COUNT), catchError(this.handleError));
  }

  // Helpers
  private makeParams(params?: QueryParams): HttpParams | undefined {
    if (!params) return undefined;
    let hp = new HttpParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== null && v !== undefined) hp = hp.set(k, String(v));
    });
    return hp;
  }

  private handleError = (error: unknown) => {
    // debugger;
    let apiError: ApiError;

    if (error instanceof HttpErrorResponse) {
      apiError = {
        status: error.status,
        message: error.error?.message || error.message || 'Unexpected error communicating with server',
        path: (error as any)?.url,
        timestamp: new Date().toISOString(),
        details: error.error,
      };
    } else {
      apiError = {
        status: 0,
        message: 'Unknown client-side error',
        timestamp: new Date().toISOString(),
      };
    }

    // Console output for now
    console.groupCollapsed(`%c!!! API Error [${apiError.status}]`, 'color: red; font-weight: bold;');
    console.log('Message:', apiError.message);
    if (apiError.path) console.log('Path:', apiError.path);
    console.log('Timestamp:', apiError.timestamp);
    console.log('Details:', apiError.details);
    console.groupEnd();

    // Rethrow
    return throwError(() => apiError);
  };
}
